import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { lightTheme, darkTheme } from '@/themes';
import { useUiStore, type ThemeMode } from '@/store/uiStore';
import { syncSystemBars, resolveEffectiveTheme } from '@/mobile/services/systemBar.service';

interface ThemeModeContextValue {
  mode: ThemeMode;
  effectiveMode: 'light' | 'dark';
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const themeMode = useUiStore((s) => s.themeMode);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const setThemeMode = useUiStore((s) => s.setThemeMode);

  const effectiveMode = useMemo(
    () => resolveEffectiveTheme(themeMode),
    [themeMode],
  );

  const theme = useMemo(
    () => (effectiveMode === 'dark' ? darkTheme : lightTheme),
    [effectiveMode],
  );

  const value = useMemo(
    () => ({ mode: themeMode, effectiveMode, toggleTheme, setThemeMode }),
    [themeMode, effectiveMode, toggleTheme, setThemeMode],
  );

  // Synchronize System Status Bar, Theme-Color, and Platform Appearance on theme changes
  useEffect(() => {
    void syncSystemBars(themeMode);

    // Also listen to OS dark/light mode preference changes if using system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleMediaChange = () => {
        const currentMode = useUiStore.getState().themeMode;
        if (currentMode !== 'dark' && currentMode !== 'light') {
          void syncSystemBars(currentMode);
        }
      };
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }
  }, [themeMode]);


  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
}
